import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Button, Chip, Checkbox, IconButton, Menu, MenuItem,
  Dialog, Slide, Typography
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { fetchData, deleteData } from "../../BaseUrl";
import DynamicTable from "../../DynamicComponent/DynamicTable";
import QuestionPaperForm from "./QuestionPaperForm";
import Toast from "../../DynamicComponent/Toast";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const fallbackRows = [
  { id: null, grade: "Grade 3", section: "A", subject: "Tamil Reading", examType: "Term 1 Exam", examDate: "2025-03-10", fileName: "reading_q1.pdf", status: "Finished" },
  { id: null, grade: "Grade 3", section: "B", subject: "Tamil Writing", examType: "Term 1 Exam", examDate: "2025-03-12", fileName: "writing_q1.pdf", status: "Upcoming" },
  { id: null, grade: "Grade 4", section: "A", subject: "Tamil Grammar", examType: "Mid Term", examDate: "2025-04-05", fileName: "grammar_mid.pdf", status: "Upcoming" },
  { id: null, grade: "Grade 4", section: "C", subject: "Tamil Speaking", examType: "Term 2 Exam", examDate: "2025-06-20", fileName: "speaking_q2.pdf", status: "Upcoming" },
  { id: null, grade: "Grade 5", section: "A", subject: "Homework", examType: "Final Exam", examDate: "2025-07-15", fileName: "homework_final.pdf", status: "Finished" },
];

const statusStyle = (status) => {
  if (status === "Finished") return { backgroundColor: "#e8f5e9", color: "#4caf50" };
  return { backgroundColor: "#fff3e0", color: "#f57c00" };
};

const actionBtnStyle = {
  border: "1px solid #D5D7DA",
  borderRadius: "8px",
  color: "#8F95B2",
  textTransform: "none",
  fontSize: "13px",
  padding: "6px 14px",
};

function ActionMenu({ row, onEdit, onDelete }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleEdit = () => { setAnchorEl(null); onEdit(row); };
  const handleDelete = () => { setAnchorEl(null); onDelete(row); };
  const handleView = () => {
    setAnchorEl(null);
    if (row.fileUrl) window.open(row.fileUrl, "_blank");
  };

  return (
    <>
      <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}><MoreVertIcon fontSize="small" /></IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={handleView}>View</MenuItem>
        <MenuItem onClick={handleEdit}>Edit</MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: "#f44336" }}>Delete</MenuItem>
      </Menu>
    </>
  );
}

export default function QuestionPaper() {
  const [rawData, setRawData] = useState([]);
  const [rows, setRows] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const handleEdit = useCallback((row) => {
    setEditRow(row);
    setFormOpen(true);
  }, []);

  const handleDeleteRequest = useCallback((row) => {
    setDeleteRow(row);
  }, []);

  const handleDeleteConfirm = async () => {
    if (!deleteRow?.id) { setDeleteRow(null); return; }
    try {
      await deleteData(`teacher/question-paper/${deleteRow.id}`);
      setToast({ open: true, severity: "success", message: "Question paper deleted" });
      setDeleteRow(null);
      loadData();
    } catch (_) {
      setToast({ open: true, severity: "error", message: "Delete failed" });
      setDeleteRow(null);
    }
  };

  const buildRows = useCallback((data, onEdit, onDelete) => {
    return data.map((r) => ({
      checkbox: <Checkbox size="small" />,
      grade: r.grade,
      section: r.section,
      subject: r.subject,
      exam: r.examType || r.exam,
      date: r.examDate ? r.examDate.substring(0, 10) : r.date,
      uploadFile: (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <PictureAsPdfIcon sx={{ color: "#f44336", fontSize: "16px" }} />
          <span
            style={{ color: "#1976d2", fontSize: "12px", cursor: r.fileUrl ? "pointer" : "default" }}
            onClick={() => r.fileUrl && window.open(r.fileUrl, "_blank")}
          >
            {r.fileName || r.file || "—"}
          </span>
        </Box>
      ),
      statusChip: (
        <Chip label={r.status} size="small" sx={{ ...statusStyle(r.status), fontWeight: "bold", fontSize: "12px", borderRadius: "20px" }} />
      ),
      action: <ActionMenu row={r} onEdit={onEdit} onDelete={onDelete} />,
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = useCallback(async () => {
    try {
      const response = await fetchData("teacher/question-paper/list");
      const data = response?.data?.papers && Array.isArray(response.data.papers)
        ? response.data.papers
        : (response?.data && Array.isArray(response.data) ? response.data : fallbackRows);
      setRawData(data);
      setRows(buildRows(data, handleEdit, handleDeleteRequest));
    } catch (_) {
      setRawData(fallbackRows);
      setRows(buildRows(fallbackRows, handleEdit, handleDeleteRequest));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildRows, handleEdit, handleDeleteRequest]);

  useEffect(() => { loadData(); }, [loadData]);

  // Rebuild rows when handlers change (to keep references fresh)
  useEffect(() => {
    if (rawData.length > 0) {
      setRows(buildRows(rawData, handleEdit, handleDeleteRequest));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawData]);

  const columns = [
    { label: "", field: "checkbox" },
    { label: "Grade", field: "grade" },
    { label: "Section", field: "section" },
    { label: "Subject", field: "subject" },
    { label: "Exam", field: "exam" },
    { label: "Date", field: "date" },
    { label: "Upload File", field: "uploadFile" },
    { label: "Status", field: "statusChip" },
    { label: "Action", field: "action" },
  ];

  return (
    <Box sx={{ padding: "1rem", textAlign: "left" }}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })} severity={toast.severity} message={toast.message} />
      <p style={{ color: "rgba(0,0,0,0.5)", fontWeight: "bold", fontSize: "14px", margin: 0 }}>
        Pages / Question Paper
      </p>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1, mb: 2, flexWrap: "wrap", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <h2 style={{ margin: 0 }}>Question Paper</h2>
          <Chip label={`${rawData.length} Records`} size="small" sx={{ backgroundColor: "#e8f5e9", color: "#4caf50", fontWeight: "bold", fontSize: "12px", borderRadius: "20px", padding: "4px 12px" }} />
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button startIcon={<DeleteOutlineIcon />} sx={actionBtnStyle}>Delete</Button>
          <Button startIcon={<FilterListIcon />} sx={actionBtnStyle}>Filters</Button>
          <Button startIcon={<FileDownloadIcon />} sx={actionBtnStyle}>Export</Button>
          <Button startIcon={<AddIcon />} onClick={() => { setEditRow(null); setFormOpen(true); }} sx={{ background: "linear-gradient(90deg,#4A7BA8,#244C77)", color: "#fff", textTransform: "none", borderRadius: "8px", padding: "8px 20px", fontSize: "13px" }}>
            Upload File
          </Button>
        </Box>
      </Box>
      <DynamicTable columns={columns} rows={rows} />

      {/* Create / Edit Dialog */}
      <Dialog open={formOpen} onClose={() => { setFormOpen(false); setEditRow(null); }} TransitionComponent={Transition} fullWidth maxWidth="sm">
        <QuestionPaperForm
          initialData={editRow}
          handleClose={() => { setFormOpen(false); setEditRow(null); loadData(); }}
        />
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={Boolean(deleteRow)} onClose={() => setDeleteRow(null)} maxWidth="xs" fullWidth>
        <Box sx={{ p: 3 }}>
          <Typography sx={{ fontWeight: "bold", fontSize: "16px", mb: 1 }}>Delete Question Paper</Typography>
          <Typography sx={{ color: "#666", fontSize: "14px", mb: 3 }}>
            Are you sure you want to delete this question paper? This action cannot be undone.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button onClick={() => setDeleteRow(null)} sx={{ color: "#666", textTransform: "none", border: "1px solid #D5D7DA", borderRadius: "8px", px: 3 }}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} sx={{ backgroundColor: "#f44336", color: "#fff", textTransform: "none", borderRadius: "8px", px: 3, "&:hover": { backgroundColor: "#d32f2f" } }}>Delete</Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
}
